import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import {
  ForgotPasswordForm,
  CheckInboxMessage,
  AuthenticationPageContainer
} from '@/features/user/components'
import { useTranslation } from 'react-i18next'
import { useSendResetLink } from '@/features/user/hooks'
import { useErrorMessageTranslation } from '@/hooks/useErrorMessageTranslation.ts'
import { UnauthenticatedPage } from '@/components/UnauthenticatedPage/UnauthenticatedPage.tsx'
import { Box, Grid, Typography } from '@mui/material'
import { PageHeader } from '@/components/PageHeader/PageHeader'

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordComponent
})

type recoveryStep = 'enterEmail' | 'checkInbox'

function ForgotPasswordComponent() {
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [userEmail, setUserEmail] = useState<string>('')
  const [currentStep, setCurrentStep] = useState<recoveryStep>('enterEmail')
  const { translateError } = useErrorMessageTranslation()
  const { t } = useTranslation(['_routes', '@feature_user'])

  const { mutate: sendResetLink } = useSendResetLink({
    onSuccess: () => {
      setErrorMessage('')
      setCurrentStep('checkInbox')
    },
    onError: error => {
      setErrorMessage(
        t('routes.forgot-password.main.otherError', { message: translateError(error) })
      )
    }
  })

  const handleSendResetLink = async (email?: string) => {
    if (email) {
      setIsLoading(true)
      sendResetLink(email)
      setUserEmail(email)
      setIsLoading(false)
    }
  }

  const handleResendEmail = async () => {
    if (userEmail) {
      sendResetLink(userEmail)
    }
  }

  const renderForgotPasswordForm = () => {
    if (currentStep === 'enterEmail') {
      return (
        <ForgotPasswordForm
          buttonTitle={t('routes.forgot-password.main.buttonTitle')}
          isLoading={isLoading}
          onSubmit={handleSendResetLink}
        />
      )
    }
  }

  return (
    <AuthenticationPageContainer
      advertisingChip={t('routes.forgot-password.main.advertising.chip')}
      advertisingTitle={t('routes.forgot-password.main.advertising.title')}
      advertisingSubtitle={t('routes.forgot-password.main.advertising.subTitle')}
    >
      {currentStep === 'enterEmail' && (
        <UnauthenticatedPage>
          <Box
            component="form"
            sx={{
              mx: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'left',
              paddingLeft: {
                sm: 6,
                xs: 0
              },
              paddingRight: {
                xl: 30,
                sm: 8,
                xs: 0
              }
            }}
          >
            <Grid
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'top'
              }}
            >
              <PageHeader backArrow={{ historyBack: true }}>
                {t('routes.forgot-password.main.title')}
              </PageHeader>
            </Grid>

            <Typography
              sx={{
                mt: 3,
                mb: 4
              }}
              variant="body2"
            >
              {t('routes.forgot-password.main.infoBand')}
            </Typography>
            {renderForgotPasswordForm()}
          </Box>
        </UnauthenticatedPage>
      )}
      {currentStep === 'checkInbox' && (
        <CheckInboxMessage error={errorMessage} onSubmit={handleResendEmail} />
      )}
    </AuthenticationPageContainer>
  )
}
